import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Package, User, RotateCcw, ShoppingCart } from 'lucide-react';
import { wholesaleHome, wholesaleOrders } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import ReorderModal from '../../components/customer/ReorderModal';
import { HeroPageContext } from '../../layouts/CustomerLayout';

const API_URL = process.env.REACT_APP_API_URL || '';
const imgSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

// Build a catalog URL with filters from a collection's filterJson string.
function buildCatalogUrl(filterJson) {
  if (!filterJson) return '/wholesale/catalog';
  try {
    const f = JSON.parse(filterJson);
    const params = new URLSearchParams();
    if (f.wineType) params.set('wineType', f.wineType);
    if (f.varietal) params.set('varietal', f.varietal);
    if (f.vintage)  params.set('vintage', String(f.vintage));
    if (f.maxPrice) params.set('maxPrice', String(f.maxPrice));
    const qs = params.toString();
    return qs ? `/wholesale/catalog?${qs}` : '/wholesale/catalog';
  } catch {
    return '/wholesale/catalog';
  }
}

// ── CollectionTile ────────────────────────────────────────────────────────────
function CollectionTile({ collection }) {
  const coverSrc = imgSrc(collection.coverImageUrl);
  return (
    <Link
      to={buildCatalogUrl(collection.filterJson)}
      className="sf-collection-tile d-block"
    >
      {coverSrc && (
        <img
          className="sf-collection-tile__img"
          src={coverSrc}
          alt={collection.name}
        />
      )}
      <div className="sf-collection-tile__label">
        <span className="sf-collection-tile__name">{collection.name}</span>
        {collection.description && (
          <span className="sf-collection-tile__desc">{collection.description}</span>
        )}
      </div>
    </Link>
  );
}

// ── FeatHeroCard ──────────────────────────────────────────────────────────────
function FeatHeroCard({ product, onDetail, onAddToCart }) {
  const src = imgSrc(product.proLabelImageUrl || product.imageUrl);
  const price = product.wholesalePrice ?? product.pricePerCase;
  return (
    <div className="sf-feat-hero">
      <div className="sf-feat-hero__img-wrap">
        {src && (
          <img
            className="sf-feat-hero__img"
            src={src}
            alt={product.proFanciful || product.proBrand}
          />
        )}
        <span className="sf-feat-hero__badge">Featured</span>
      </div>
      <div className="sf-feat-hero__body">
        <div className="sf-feat-hero__eyebrow">
          {[product.proVintage, product.proAppellation].filter(Boolean).join(' · ')}
        </div>
        <div className="sf-feat-hero__name">{product.proFanciful || product.proBrand}</div>
        <div className="sf-feat-hero__sub">{product.proBrand} · {product.proVarietal}</div>
        <div className="sf-feat-hero__footer">
          <div>
            {price && (
              <div className="sf-feat-hero__price">
                ${Number(price).toFixed(2)}
                <span className="sf-feat-hero__price-label"> /case</span>
              </div>
            )}
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => onDetail(product)}
            >
              Details
            </button>
            <button
              className="btn btn-sm"
              style={{ background: 'var(--sf-primary)', color: '#fff' }}
              onClick={() => onAddToCart(product)}
            >
              <ShoppingCart size={13} className="me-1" /> Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── FeatSideCard ──────────────────────────────────────────────────────────────
function FeatSideCard({ product, onDetail, onAddToCart }) {
  const src = imgSrc(product.proLabelImageUrl || product.imageUrl);
  const price = product.wholesalePrice ?? product.pricePerCase;
  return (
    <div className="sf-feat-card" onClick={() => onDetail(product)}>
      <div className="sf-feat-card__img-wrap">
        {src && (
          <img
            className="sf-feat-card__img"
            src={src}
            alt={product.proFanciful || product.proBrand}
          />
        )}
      </div>
      <div className="sf-feat-card__body">
        <div className="sf-feat-card__eyebrow">
          {[product.proVintage, product.proVarietal].filter(Boolean).join(' · ')}
        </div>
        <div className="sf-feat-card__name">{product.proFanciful || product.proBrand}</div>
        <div className="sf-feat-card__row">
          {price && (
            <span className="sf-feat-card__price">
              ${Number(price).toFixed(2)}
              <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--sf-muted)' }}>/cs</span>
            </span>
          )}
          <button
            className="btn btn-sm py-0 px-2"
            style={{ fontSize: '0.75rem', color: 'var(--sf-primary)', border: '1px solid var(--sf-primary)' }}
            onClick={e => { e.stopPropagation(); onAddToCart(product); }}
          >
            + Cart
          </button>
        </div>
      </div>
    </div>
  );
}

// ── StorefrontHome ────────────────────────────────────────────────────────────
export default function StorefrontHome() {
  const navigate = useNavigate();
  const setIsHeroPage = useContext(HeroPageContext);
  const { cartCount, addToCart } = useCart();

  const [loading, setLoading]   = useState(true);
  const [homeData, setHomeData] = useState(null);
  const [reorderModal, setReorderModal] = useState({ show: false, loading: false, data: null });

  // Signal hero page — makes the navbar transparent over the banner
  useEffect(() => {
    setIsHeroPage(true);
    return () => setIsHeroPage(false);
  }, [setIsHeroPage]);

  const handleReorderClick = async () => {
    setReorderModal({ show: true, loading: true, data: null });
    try {
      const res = await wholesaleOrders.getLastOrderItems();
      setReorderModal({ show: true, loading: false, data: res.data.data });
    } catch {
      setReorderModal({ show: true, loading: false, data: null });
    }
  };

  const handleReorderConfirm = async (items) => {
    for (const item of items) {
      addToCart(item.productID, item.quantity);
    }
    setReorderModal({ show: false, loading: false, data: null });
    navigate('/wholesale/checkout');
  };

  // Fetch storefront data
  useEffect(() => {
    wholesaleHome.get()
      .then(r => setHomeData(r.data.data))
      .catch(() => setHomeData({ banner: {}, featuredProducts: [], collections: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '60vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  const {
    banner           = {},
    featuredProducts = [],
    collections      = [],
  } = homeData || {};

  const bannerImgSrc = imgSrc(banner.imageUrl);

  return (
    <>
      {/* ── Section 1: Hero Banner ─────────────────────────────────────────── */}
      <div className="sf-hero">
        {bannerImgSrc && (
          <img
            className="sf-hero__img"
            src={bannerImgSrc}
            alt=""
            aria-hidden="true"
          />
        )}
        <div className="sf-hero__overlay">
          <div className="container">
            {banner.headline && (
              <h1 className="sf-hero__headline">{banner.headline}</h1>
            )}
            {banner.subheadline && (
              <p className="sf-hero__sub">{banner.subheadline}</p>
            )}
            {banner.ctaLabel && (
              <Link
                to={banner.ctaUrl || '/wholesale/catalog'}
                className="sf-hero__cta"
              >
                {banner.ctaLabel}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ── Section 2: Quick-Access Strip ─────────────────────────────────── */}
      <div className="sf-quick">
        <div className="sf-quick__inner">
          <Link to="/wholesale/orders" className="sf-quick__link">
            <Package size={14} /> My Orders
          </Link>
          <Link to="/wholesale/checkout" className="sf-quick__link">
            <ShoppingCart size={14} /> My Cart
            {cartCount > 0 && <span className="sf-quick__badge">{cartCount}</span>}
          </Link>
          <Link to="/wholesale/account" className="sf-quick__link">
            <User size={14} /> My Account
          </Link>
          {/* Reorder Last Purchase */}
          <button
            className="sf-quick__link"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={handleReorderClick}
          >
            <RotateCcw size={14} /> Reorder Last Purchase <ArrowRight size={12} />
          </button>
        </div>
      </div>

      {/* ── Section 3: Editorial Featured Products ────────────────────────── */}
      {featuredProducts.length > 0 && (
        <div className="sf-section">
          <div className="sf-section-header">
            <h2 className="sf-section-title">Current Selection</h2>
            <Link to="/wholesale/catalog" className="sf-section-viewall">
              View Full Catalog <ArrowRight size={12} />
            </Link>
          </div>
          <div className="sf-editorial">
            {/* Hero card — first featured product */}
            <FeatHeroCard
              product={featuredProducts[0]}
              onDetail={p => navigate(`/wholesale/products/${p.productID}`)}
              onAddToCart={addToCart}
            />
            {/* Compact side cards — remaining featured products (up to 3) */}
            {featuredProducts.slice(1, 4).map(product => (
              <FeatSideCard
                key={product.productID}
                product={product}
                onDetail={p => navigate(`/wholesale/products/${p.productID}`)}
                onAddToCart={addToCart}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Section 4: Winemaker Video ────────────────────────────────────── */}
      {banner.videoUrl && (
        <div className="sf-video">
          <div className="sf-video__embed">
            <iframe
              src={banner.videoUrl}
              title="Winemaker Introduction"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <div className="sf-video__copy">
            <div className="sf-video__eyebrow">From the Winemaker</div>
            <div className="sf-video__headline">
              {banner.videoHeadline || 'Lodi-grown. Handcrafted. Yours to pour.'}
            </div>
            <div className="sf-video__body">
              {banner.videoBody || 'Learn about the stories behind our estate and partner vineyards, vintage highlights, and what makes each wine a standout for your program.'}
            </div>
            <Link to="/wholesale/catalog" className="sf-video__cta">
              Shop the Collection <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}

      {/* ── Section 5: Collections Grid ───────────────────────────────────── */}
      {collections.length > 0 && (
        <div className="sf-section" style={{ background: 'var(--sf-bg, #fdfaf7)', paddingBottom: '4rem' }}>
          <div className="sf-section-header">
            <h2 className="sf-section-title">Shop By</h2>
          </div>
          <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3">
            {collections.map(col => (
              <div className="col" key={col.collectionID}>
                <CollectionTile collection={col} />
              </div>
            ))}
          </div>
        </div>
      )}
      <ReorderModal
        show={reorderModal.show}
        loading={reorderModal.loading}
        data={reorderModal.data}
        onClose={() => setReorderModal({ show: false, loading: false, data: null })}
        onConfirm={handleReorderConfirm}
      />
    </>
  );
}
